import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Phone, Plus, Edit, Trash2, Settings, Server,
  Eye, EyeOff, Save, CheckCircle, XCircle, Loader2,
  Building2, Key, PhoneCall
} from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/utils/userHelper';

export default function PBXManagement({ user: propUser }) {
  const user = useUser(propUser);
  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [activeTab, setActiveTab] = useState('server');
  
  // SIP Server Config
  const [sipConfig, setSipConfig] = useState({
    websocket_url: '',
    sip_domain: '',
    sip_port: 5060,
    stun_servers: ['stun:stun.l.google.com:19302'],
    turn_servers: [],
    ice_transport_policy: 'all',
    register_expires: 600
  });
  const [configSaving, setConfigSaving] = useState(false);
  
  // Extensions - simplified
  const [extensions, setExtensions] = useState([]);
  const [extensionDialog, setExtensionDialog] = useState(false);
  const [editingExtension, setEditingExtension] = useState(null);
  const [extensionForm, setExtensionForm] = useState({
    name: '',
    extension: '',
    sip_username: '',
    sip_password: '',
    sip_port: 5060,
    is_public: true  // Show in guest directory by default
  });
  const [showPassword, setShowPassword] = useState(false);
  const [extensionSaving, setExtensionSaving] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchHotels();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && selectedHotel) {
      fetchSipConfig();
      fetchExtensions();
    }
  }, [isSuperAdmin, selectedHotel]);

  const fetchHotels = async () => {
    try {
      setLoading(true);
      const res = await api.get('/super-admin/hotels');
      const hotelsData = res.data || [];
      setHotels(hotelsData);
      if (hotelsData.length > 0) {
        setSelectedHotel(hotelsData[0]);
      }
    } catch (error) {
      console.error('Failed to load hotels:', error);
      toast.error('Failed to load hotels');
    } finally {
      setLoading(false);
    }
  };

  const fetchSipConfig = async () => {
    if (!selectedHotel?.id) return;
    try {
      const res = await api.get(`/sip/config/${selectedHotel.id}`);
      if (res.data) {
        setSipConfig({
          websocket_url: res.data.websocket_url || '',
          sip_domain: res.data.sip_domain || '',
          sip_port: res.data.sip_port || 5060,
          stun_servers: res.data.stun_servers || ['stun:stun.l.google.com:19302'],
          turn_servers: res.data.turn_servers || [],
          ice_transport_policy: res.data.ice_transport_policy || 'all',
          register_expires: res.data.register_expires || 600
        });
      } else {
        setSipConfig({
          websocket_url: '',
          sip_domain: '',
          sip_port: 5060,
          stun_servers: ['stun:stun.l.google.com:19302'],
          turn_servers: [],
          ice_transport_policy: 'all',
          register_expires: 600
        });
      }
    } catch (error) {
      console.error('Failed to fetch SIP config:', error);
    }
  };

  const fetchExtensions = async () => {
    if (!selectedHotel?.id) return;
    try {
      const res = await api.get(`/sip/directory/hotel/${selectedHotel.id}`);
      setExtensions(res.data || []);
    } catch (error) {
      console.error('Failed to fetch extensions:', error);
      setExtensions([]);
    }
  };

  const saveSipConfig = async () => {
    if (!selectedHotel?.id) return;
    
    if (!sipConfig.websocket_url || !sipConfig.sip_domain) {
      toast.error('WebSocket URL and SIP Domain are required');
      return;
    }
    
    setConfigSaving(true);
    try {
      await api.post(`/sip/config/${selectedHotel.id}`, sipConfig);
      toast.success('SIP Server configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const openAddExtension = () => {
    setEditingExtension(null);
    setExtensionForm({
      name: '',
      extension: '',
      sip_username: '',
      sip_password: '',
      sip_port: sipConfig.sip_port || 5060,
      is_public: true  // Show in guest directory by default
    });
    setExtensionDialog(true);
  };

  const openEditExtension = async (ext) => {
    try {
      const res = await api.get(`/sip/directory/${ext.id}`);
      setEditingExtension(res.data);
      setExtensionForm({
        name: res.data.name || '',
        extension: res.data.extension || '',
        sip_username: res.data.sip_username || '',
        sip_password: res.data.sip_password || '',
        sip_port: res.data.sip_port || 5060,
        is_public: res.data.is_public !== false  // Default to true if not set
      });
      setExtensionDialog(true);
    } catch (error) {
      toast.error('Failed to load extension details');
    }
  };

  const saveExtension = async () => {
    if (!extensionForm.name || !extensionForm.extension) {
      toast.error('Name and Extension Number are required');
      return;
    }
    
    if (!extensionForm.sip_username || !extensionForm.sip_password) {
      toast.error('SIP User ID and Password are required');
      return;
    }
    
    setExtensionSaving(true);
    try {
      const formData = {
        name: extensionForm.name,
        extension: extensionForm.extension,
        sip_username: extensionForm.sip_username,
        sip_password: extensionForm.sip_password,
        sip_port: extensionForm.sip_port,
        is_active: true,
        is_public: extensionForm.is_public  // Use form value
      };
      
      if (editingExtension) {
        await api.put(`/sip/directory/${editingExtension.id}`, formData);
        toast.success('Extension updated');
      } else {
        await api.post(`/sip/directory/hotel/${selectedHotel.id}`, formData);
        toast.success('Extension created');
      }
      
      setExtensionDialog(false);
      fetchExtensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save extension');
    } finally {
      setExtensionSaving(false);
    }
  };

  const deleteExtension = async (id) => {
    if (!window.confirm('Are you sure you want to delete this extension?')) return;
    
    try {
      await api.delete(`/sip/directory/${id}`);
      toast.success('Extension deleted');
      fetchExtensions();
    } catch (error) {
      toast.error('Failed to delete extension');
    }
  };

  // Access denied for non-super-admin
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Server className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500">Only Super Admin can access PBX Management</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pbx-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Server className="h-7 w-7 text-primary" />
            PBX Management
          </h1>
          <p className="text-gray-500 mt-1">Configure SIP servers and manage extensions for hotels</p>
        </div>
      </div>

      {/* Hotel Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label>Select Hotel</Label>
              <Select
                value={selectedHotel?.id || ''}
                onValueChange={(v) => {
                  const hotel = hotels.find(h => h.id === v);
                  setSelectedHotel(hotel);
                }}
              >
                <SelectTrigger className="mt-1" data-testid="hotel-selector">
                  <SelectValue placeholder="Select a hotel" />
                </SelectTrigger>
                <SelectContent>
                  {hotels.map((hotel) => (
                    <SelectItem key={hotel.id} value={hotel.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {hotel.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="pt-6">
              <Badge variant={sipConfig.websocket_url ? "default" : "secondary"} className={sipConfig.websocket_url ? "bg-green-100 text-green-700" : ""}>
                {sipConfig.websocket_url ? (
                  <><CheckCircle className="h-3 w-3 mr-1" /> SIP Configured</>
                ) : (
                  <><XCircle className="h-3 w-3 mr-1" /> Not Configured</>
                )}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedHotel && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="server" className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              SIP Server Configuration
            </TabsTrigger>
            <TabsTrigger value="extensions" className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Extensions ({extensions.length})
            </TabsTrigger>
          </TabsList>

          {/* SIP Server Configuration Tab */}
          <TabsContent value="server" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  SIP Server Settings
                </CardTitle>
                <CardDescription>
                  Configure the SIP server connection for {selectedHotel.name}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>WebSocket URL *</Label>
                    <Input
                      value={sipConfig.websocket_url}
                      onChange={(e) => setSipConfig({ ...sipConfig, websocket_url: e.target.value })}
                      placeholder="wss://sip.example.com:8089/ws"
                      data-testid="sip-websocket-url"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SIP Domain *</Label>
                    <Input
                      value={sipConfig.sip_domain}
                      onChange={(e) => setSipConfig({ ...sipConfig, sip_domain: e.target.value })}
                      placeholder="sip.example.com"
                      data-testid="sip-domain"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Default SIP Port</Label>
                    <Input
                      type="number"
                      value={sipConfig.sip_port}
                      onChange={(e) => setSipConfig({ ...sipConfig, sip_port: parseInt(e.target.value) || 5060 })}
                      placeholder="5060"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Register Expiry (seconds)</Label>
                    <Input
                      type="number"
                      value={sipConfig.register_expires}
                      onChange={(e) => setSipConfig({ ...sipConfig, register_expires: parseInt(e.target.value) || 600 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ICE Transport</Label>
                    <Select
                      value={sipConfig.ice_transport_policy}
                      onValueChange={(v) => setSipConfig({ ...sipConfig, ice_transport_policy: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All (STUN + TURN)</SelectItem>
                        <SelectItem value="relay">Relay Only (TURN)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>STUN Servers</Label>
                  <Input
                    value={(sipConfig.stun_servers || []).join(', ')}
                    onChange={(e) => setSipConfig({ 
                      ...sipConfig, 
                      stun_servers: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                    placeholder="stun:stun.l.google.com:19302"
                  />
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={saveSipConfig} disabled={configSaving} data-testid="save-sip-config">
                    {configSaving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save SIP Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Extensions Tab */}
          <TabsContent value="extensions" className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5" />
                    Extensions for {selectedHotel.name}
                  </CardTitle>
                  <CardDescription>
                    Create SIP extensions with credentials
                  </CardDescription>
                </div>
                <Button onClick={openAddExtension} data-testid="add-extension">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Extension
                </Button>
              </CardHeader>
              <CardContent>
                {extensions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <PhoneCall className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No extensions configured</p>
                    <p className="text-sm">Add your first extension to get started</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Extension</TableHead>
                        <TableHead>SIP User ID</TableHead>
                        <TableHead>SIP Port</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {extensions.map((ext) => (
                        <TableRow key={ext.id}>
                          <TableCell className="font-medium">{ext.name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{ext.extension}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{ext.sip_username || '-'}</TableCell>
                          <TableCell>{ext.sip_port || 5060}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditExtension(ext)}
                                data-testid={`edit-ext-${ext.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteExtension(ext.id)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-ext-${ext.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Add/Edit Extension Dialog - Simplified */}
      <Dialog open={extensionDialog} onOpenChange={setExtensionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              {editingExtension ? 'Edit Extension' : 'Add Extension'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Front Desk"
                value={extensionForm.name}
                onChange={(e) => setExtensionForm({ ...extensionForm, name: e.target.value })}
                data-testid="ext-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Extension Number *</Label>
              <Input
                placeholder="100"
                value={extensionForm.extension}
                onChange={(e) => setExtensionForm({ ...extensionForm, extension: e.target.value })}
                data-testid="ext-extension"
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Key className="h-4 w-4" />
                SIP Credentials
              </h4>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>SIP User ID *</Label>
                  <Input
                    placeholder="ext100"
                    value={extensionForm.sip_username}
                    onChange={(e) => setExtensionForm({ ...extensionForm, sip_username: e.target.value })}
                    data-testid="ext-sip-username"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={extensionForm.sip_password}
                      onChange={(e) => setExtensionForm({ ...extensionForm, sip_password: e.target.value })}
                      data-testid="ext-sip-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1 h-7 w-7 p-0"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    placeholder="5060"
                    value={extensionForm.sip_port}
                    onChange={(e) => setExtensionForm({ ...extensionForm, sip_port: parseInt(e.target.value) || 5060 })}
                    data-testid="ext-sip-port"
                  />
                </div>

                {/* Directory Visibility Checkbox */}
                <div className="flex items-center space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={extensionForm.is_public}
                    onChange={(e) => setExtensionForm({ ...extensionForm, is_public: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    data-testid="ext-is-public"
                  />
                  <Label htmlFor="is_public" className="text-sm font-medium text-gray-700">
                    Show in Guest Directory
                  </Label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  When enabled, guests can see this extension in the phone directory on the Guest Portal
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExtensionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveExtension} disabled={extensionSaving} data-testid="save-extension">
              {extensionSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {editingExtension ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
